/**
 * Add or remove a passkey (v11.0.0) — the same bottom sheet as the rest of the
 * app, so focus trap, Escape and backdrop dismissal behave identically.
 *
 * ADD is TWO taps, on purpose. Tap one ("Continue") sends the current password
 * and fetches the passkey options. Tap two ("Save passkey") calls the platform
 * as its FIRST await. Safari ties the passkey sheet to the user's tap, and a
 * network round trip between the tap and the call can spend it — so the
 * request the passkey needs is made before the tap that uses it
 * (docs/SECURITY.md §14).
 *
 * Both modes re-ask the current password: a change to how the account signs in
 * needs more than a session (ASVS 5.0 V7.5.1). Both say what the password does
 * afterwards — it keeps working (owner's recovery plan, 11 Oct 2026).
 */

import { trapFocus } from '../lib/dialog-trap';

export type PasskeySheetOptions =
  | {
      mode: 'add';
      /** Check the password and fetch options. Rejecting keeps step one open. */
      onContinue: (currentPassword: string, name: string) => Promise<void>;
      /**
       * Create and send the passkey. Resolve = done (the caller closes the sheet).
       * Reject with `{ restart: true }` when the options are spent and step one
       * must run again; any other rejection stays on step two to retry.
       */
      onSave: () => Promise<void>;
      describeError: (err: unknown) => string;
      onDismiss: () => void;
    }
  | {
      mode: 'remove';
      passkeyName: string;
      onRemove: (currentPassword: string) => Promise<void>;
      describeError: (err: unknown) => string;
      onDismiss: () => void;
    };

/** Thrown by onSave when step one has to run again. */
export class RestartEnrolment extends Error {
  constructor(readonly reason: unknown) {
    super('restart');
  }
}

function labelled(id: string, text: string, input: HTMLInputElement) {
  const lbl = document.createElement('label');
  lbl.className = 'sheet-field-label';
  lbl.htmlFor = id;
  lbl.textContent = text;
  input.id = id;
  input.className = 'sheet-input';
  input.autocapitalize = 'none';
  input.spellcheck = false;
  input.setAttribute('autocorrect', 'off');
  return lbl;
}

export function createPasskeySheet(opts: PasskeySheetOptions): HTMLElement {
  const backdrop = document.createElement('div');
  backdrop.className = 'sheet-backdrop';

  const sheet = document.createElement('div');
  sheet.className = 'sheet passkey-sheet';
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-labelledby', 'passkey-sheet-title');

  const title = document.createElement('h2');
  title.id = 'passkey-sheet-title';
  title.className = 'sheet-title';

  const sub = document.createElement('p');
  sub.className = 'sheet-sub';

  const form = document.createElement('form');
  form.className = 'sheet-form';
  form.noValidate = true;

  const password = document.createElement('input');
  password.type = 'password';
  password.autocomplete = 'current-password';
  const passwordLabel = labelled('passkey-password', 'Current password', password);

  const message = document.createElement('p');
  message.className = 'sheet-message';
  message.setAttribute('role', 'alert');

  const actions = document.createElement('div');
  actions.className = 'sheet-actions';

  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'btn-quiet';
  cancel.addEventListener('click', () => opts.onDismiss());

  const confirm = document.createElement('button');
  confirm.type = 'submit';
  confirm.className = 'btn-primary';
  confirm.disabled = true;
  actions.append(cancel, confirm);

  const busy = (on: boolean, text: string) => {
    confirm.disabled = on;
    confirm.textContent = text;
  };

  if (opts.mode === 'remove') {
    title.textContent = `Remove “${opts.passkeyName}”?`;
    sub.textContent =
      'It stops working straight away, on every device it is saved to. Your password still works.';
    cancel.textContent = 'Keep it';
    confirm.textContent = 'Remove passkey';
    password.addEventListener('input', () => {
      message.textContent = '';
      confirm.disabled = password.value === '';
    });
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (confirm.disabled) return;
      busy(true, 'Removing…');
      try {
        await opts.onRemove(password.value);
      } catch (err) {
        message.textContent = opts.describeError(err);
        busy(false, 'Remove passkey');
      }
    });
    form.append(passwordLabel, password, message, actions);
  } else {
    const name = document.createElement('input');
    name.type = 'text';
    name.autocomplete = 'off';
    name.maxLength = 40;
    name.placeholder = 'iPhone · Bitwarden';
    const nameLabel = labelled('passkey-name', 'Name (so you can tell it apart)', name);
    // One wrapper hidden by inline style: `.sheet-input` sets its own display,
    // which would beat the `hidden` attribute on the inputs themselves.
    const fields = document.createElement('div');
    fields.className = 'passkey-fields';
    fields.append(nameLabel, name, passwordLabel, password);

    const stepOne = () => {
      title.textContent = 'Add a passkey';
      sub.textContent =
        'Bitwarden or your iPhone’s own Passwords saves it — iOS asks which — and asks for your passcode each time. Add one of each if you like. Your password keeps working too.';
      cancel.textContent = 'Not now';
      confirm.textContent = 'Continue';
      fields.style.display = '';
      confirm.disabled = password.value === '';
      form.dataset.step = 'details';
    };
    const stepTwo = () => {
      title.textContent = 'Save your passkey';
      sub.textContent = 'Tap Save passkey, then confirm in your passkey app.';
      fields.style.display = 'none';
      confirm.disabled = false;
      confirm.textContent = 'Save passkey';
      form.dataset.step = 'save';
      confirm.focus();
    };

    password.addEventListener('input', () => {
      message.textContent = '';
      if (form.dataset.step === 'details') confirm.disabled = password.value === '';
    });
    name.addEventListener('input', () => {
      message.textContent = '';
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (confirm.disabled) return;
      message.textContent = '';
      if (form.dataset.step === 'details') {
        busy(true, 'Checking…');
        try {
          await opts.onContinue(password.value, name.value);
          password.value = '';
          stepTwo();
        } catch (err) {
          message.textContent = opts.describeError(err);
          busy(false, 'Continue');
          confirm.disabled = password.value === '';
        }
        return;
      }
      busy(true, 'Saving…');
      try {
        // The platform call is the first await inside onSave (see header).
        await opts.onSave();
      } catch (err) {
        if (err instanceof RestartEnrolment) {
          stepOne();
          message.textContent = opts.describeError(err.reason);
          password.focus();
          return;
        }
        message.textContent = opts.describeError(err);
        busy(false, 'Save passkey');
      }
    });

    form.append(fields, message, actions);
    stepOne();
  }

  sheet.append(title, sub, form);
  backdrop.append(sheet);
  trapFocus({ backdrop, sheet, onDismiss: opts.onDismiss });
  queueMicrotask(() => password.focus());
  return backdrop;
}
